import { InfiniteData, QueryKey, useMutation } from '@tanstack/react-query';
import { useToast } from '@givebutter/primitives/toast';
import { FetchTasksParams, UpdateTaskParams, updateTask } from 'api/tasks';
import { transformSavedFilters } from 'components/filter/filters-next/downstream';
import {
    TaskFilterStoreValues,
    useTasksFilterStore,
} from 'components/tasks/utils/useTasksFilterStore';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { toServer } from 'helpers/dates';
import { orderBy, uniqBy } from 'lodash';
import { DateTime } from 'luxon';
import { accountQueryKeys } from 'queries/account-query-keys';
import { queryClient } from 'queries/queryClient';
import { Task } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';
import { PaginatedData } from 'types/pagination';

const useUpdateTask = () => {
    const { accountId } = useCurrentAccount();
    const { error } = useToast();
    const filters = useTasksFilterStore(({ sortBy, sortByDesc }) => ({
        sortBy,
        sortByDesc,
    }));

    return useMutation<
        Task,
        GivebutterAxiosError,
        { taskId: number; params: UpdateTaskParams },
        { originalTask: Task | undefined }
    >({
        mutationFn: ({ taskId, params }) => {
            return updateTask(accountId, taskId, params);
        },
        onMutate: ({ taskId, params }) => {
            const originalTask = queryClient.getQueryData<Task>(
                accountQueryKeys.taskById(accountId, taskId),
            );

            const { links, ...restParams } = params;

            if (originalTask) {
                queryClient.setQueryData<Task>(
                    accountQueryKeys.taskById(accountId, taskId),
                    oldTask => {
                        if (!oldTask) return oldTask;
                        return {
                            ...oldTask,
                            ...restParams,
                        };
                    },
                );
            }

            const listQueries = queryClient.getQueriesData<InfiniteData<PaginatedData<Task>>>({
                queryKey: accountQueryKeys.tasksWithFilters(accountId, {}),
                exact: false,
            });

            // optimistically update our task lists cache if status was changed
            if (listQueries.length && !!restParams.status) {
                optimisticallyUpdateListCaches({
                    queries: listQueries,
                    taskId,
                    params: restParams,
                    filters,
                });
            }

            return { originalTask };
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: accountQueryKeys.tasks(accountId) });
        },
        onError: (e, { taskId }, context) => {
            error(e.response?.data.message || 'Unable to update task.');

            if (context?.originalTask) {
                queryClient.setQueryData(
                    accountQueryKeys.taskById(accountId, taskId),
                    () => context.originalTask,
                );
            }
        },
    });
};

interface OptimisticallyUpdateListCachesArgs {
    queries: [QueryKey, InfiniteData<PaginatedData<Task>, unknown> | undefined][];
    taskId: number;
    params: UpdateTaskParams;
    filters: {
        sortBy: TaskFilterStoreValues['sortBy'];
        sortByDesc: TaskFilterStoreValues['sortByDesc'];
    };
}
const optimisticallyUpdateListCaches = ({
    queries,
    taskId,
    params,
    filters,
}: OptimisticallyUpdateListCachesArgs) => {
    for (const [queryKey, cachedData] of queries) {
        if (!cachedData) continue;

        const allTasks = cachedData.pages.flatMap(p => p.data);
        const changedTask = allTasks.find(t => t.id === taskId);
        if (!changedTask || changedTask.status === params.status) continue;

        const dataWithRemoved = {
            ...cachedData,
            pages: cachedData.pages.map(page => ({
                ...page,
                data: page.data.filter(t => t.id !== taskId),
            })),
        };
        queryClient.setQueryData(queryKey, dataWithRemoved);

        const queriesToAdd = queries.filter(([key]) => {
            const fetchTaskParams = key.at(-1) as FetchTasksParams;
            if (!fetchTaskParams?.filters) return false;

            const transformedFilters = transformSavedFilters(fetchTaskParams?.filters);
            const statusFilter = transformedFilters.find(f => f.serverKey === 'status');
            if (!statusFilter) return false;

            return params.status && (statusFilter.value as string[]).includes(params.status);
        });

        for (const [key, data] of queriesToAdd) {
            const dataWithAdded = {
                ...data,
                pages: data?.pages.map(page => {
                    let newTasks = [
                        ...page.data,
                        {
                            ...changedTask,
                            ...params,
                            ...(params.status === 'complete'
                                ? { completed_at: toServer(DateTime.now()).toSQL() }
                                : {}),
                        },
                    ];

                    if (filters.sortBy) {
                        newTasks = orderBy(newTasks, filters.sortBy, 'asc');
                    }

                    if (filters.sortByDesc) {
                        newTasks = orderBy(newTasks, filters.sortByDesc, 'desc');
                    }

                    return {
                        ...page,
                        data: uniqBy(newTasks, 'id'),
                    };
                }),
            };
            queryClient.setQueryData(key, dataWithAdded);
        }
    }
};

export { useUpdateTask };
